export interface ODataQuery {
  $select?: string;
  $filter?: string;
  $orderby?: string;
  $top?: number;
  $skip?: number;
  $expand?: string;
  $count?: boolean;
}

export interface ODataResponse<T> {
  value: T[];
  '@odata.count'?: number;
  '@odata.nextLink'?: string;
}

export class DataverseClient {
  private baseUrl: string;
  private getToken: () => Promise<string>;

  constructor(orgUrl: string, getToken: () => Promise<string>) {
    this.baseUrl = `${orgUrl}/api/data/v9.2`;
    this.getToken = getToken;
  }

  private async getHeaders(): Promise<HeadersInit> {
    const token = await this.getToken();
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'OData-MaxVersion': '4.0',
      'OData-Version': '4.0',
      Accept: 'application/json',
      Prefer: 'odata.include-annotations="*"',
    };
  }

  async get<T>(entity: string, query?: ODataQuery): Promise<ODataResponse<T>> {
    const params = new URLSearchParams();
    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined) {
          params.set(key, String(value));
        }
      });
      if (query.$count) {
        params.set('$count', 'true');
      }
    }
    const qs = params.toString();
    const response = await fetch(`${this.baseUrl}/${entity}${qs ? `?${qs}` : ''}`, {
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      throw new Error(`OData GET error ${response.status}: ${await response.text()}`);
    }
    return response.json() as Promise<ODataResponse<T>>;
  }

  async getById<T>(entity: string, id: string, query?: Pick<ODataQuery, '$select' | '$expand'>): Promise<T> {
    const params = new URLSearchParams();
    if (query?.$select) params.set('$select', query.$select);
    if (query?.$expand) params.set('$expand', query.$expand);
    const qs = params.toString();
    const response = await fetch(`${this.baseUrl}/${entity}(${id})${qs ? `?${qs}` : ''}`, {
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      throw new Error(`OData GET ${id} error ${response.status}`);
    }
    return response.json() as Promise<T>;
  }

  async create<T>(entity: string, data: Partial<T>): Promise<T> {
    const response = await fetch(`${this.baseUrl}/${entity}`, {
      method: 'POST',
      headers: { ...(await this.getHeaders()), Prefer: 'return=representation' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(`OData POST error ${response.status}: ${await response.text()}`);
    }
    return response.json() as Promise<T>;
  }

  async update<T>(entity: string, id: string, data: Partial<T>): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${entity}(${id})`, {
      method: 'PATCH',
      headers: await this.getHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(`OData PATCH error ${response.status}: ${await response.text()}`);
    }
  }

  async delete(entity: string, id: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${entity}(${id})`, {
      method: 'DELETE',
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      throw new Error(`OData DELETE error ${response.status}`);
    }
  }

  async count(entity: string, filter?: string): Promise<number> {
    const params = new URLSearchParams({ $count: 'true', $top: '1' });
    if (filter) params.set('$filter', filter);
    const response = await fetch(`${this.baseUrl}/${entity}?${params.toString()}`, {
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      throw new Error(`OData count error ${response.status}`);
    }
    const json = (await response.json()) as { '@odata.count': number };
    return json['@odata.count'] ?? 0;
  }
}

const memoryStore = new Map<string, string>();
const keyOf = (entity: string) => `crm-app:${entity}`;

export const isDataverseConfigured = (): boolean => {
  return Boolean(import.meta.env.VITE_CLIENT_ID && import.meta.env.VITE_TENANT_ID && import.meta.env.VITE_DATAVERSE_URL);
};

export const createId = (prefix: string): string => {
  const random = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.round(Math.random() * 10000)}`;
  return `${prefix}-${random}`;
};

export const delay = async (ms = 120): Promise<void> => {
  await new Promise((resolve) => window.setTimeout(resolve, ms));
};

const getStoredValue = (entity: string): string | null => {
  if (typeof window !== 'undefined') {
    return window.localStorage.getItem(keyOf(entity));
  }
  return memoryStore.get(keyOf(entity)) ?? null;
};

const setStoredValue = (entity: string, value: string): void => {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(keyOf(entity), value);
    return;
  }
  memoryStore.set(keyOf(entity), value);
};

export const readCollection = <T>(entity: string): T[] => {
  const raw = getStoredValue(entity);
  if (!raw) {
    return [];
  }
  try {
    return JSON.parse(raw) as T[];
  } catch {
    return [];
  }
};

export const writeCollection = <T>(entity: string, items: T[]): void => {
  setStoredValue(entity, JSON.stringify(items));
};

export const seedCollection = <T>(entity: string, items: T[]): void => {
  if (readCollection<T>(entity).length === 0) {
    writeCollection(entity, items);
  }
};
